import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { LoginManager } from '../../core/login-manager';
import { UtentiManager } from '../../lib/utenti-manager';
import { formatDateTime, nomeUtente, refId, optPayload, refPayload } from '../../lib/format';
import {
    ADMIN_GLOBALE, CONDOMINO, etichettaLivello, isAdminGlobale, livelloDi,
    livelliAssegnabili, richiedeApprovazione
} from '../../lib/ruoli';
import {
    useEntityList, useEntityItem, statoLista, Field, DetailPage, DetailActions,
    ConfirmModal, messaggioErrore
} from './common';
import Select from '../../components/Select';

const VUOTO = {
    name: '', surname: '', email: '', telefono: '', password: '',
    level: CONDOMINO, idUserGroup: 0, idPalazzina: 0, idAppartamento: 0, abilitato: 1, note: ''
};

/**
 * Pagina di un utente: creazione se la rotta e' `/admin/utenti/nuovo`,
 * modifica se porta un id.
 *
 * Gli amministratori, globali o di condominio, non passano dall'approvazione:
 * per loro lo stato non e' una scelta.
 *
 * Il condominio lo sceglie solo l'amministratore globale, ed e' l'unico campo
 * che non viaggia con il resto del form: la CRUD non accetta `idUserGroup`, lo
 * timbra da se' con il gruppo di chi scrive. Ci pensa `assegna_utente` subito
 * dopo il salvataggio.
 */
export default function Utente() {
    const { id } = useParams();
    const navigate = useNavigate();
    const utenteCorrente = LoginManager.getUserInfo();

    const globale = isAdminGlobale(utenteCorrente);

    const nuovo = !id || id === 'nuovo';
    const { item, loading, error, reload } = useEntityItem('user', nuovo ? null : id);
    const palazzine = useEntityList('palazzina', 'nome', 'asc');
    const appartamenti = useEntityList('appartamento', 'identificativo', 'asc');
    // solo l'amministratore globale sceglie: gli altri hanno un condominio solo
    const condomini = useEntityList(globale ? 'user_group' : null, 'name', 'asc');

    const [form, setForm] = useState(nuovo ? { ...VUOTO } : null);
    const [saving, setSaving] = useState(false);
    const [daEliminare, setDaEliminare] = useState(false);
    const [working, setWorking] = useState(false);
    const [daInviare, setDaInviare] = useState(false);
    const [inviando, setInviando] = useState(false);

    useEffect(() => {
        if (!item) return;
        setForm({
            name: item.name || '',
            surname: item.surname || '',
            email: item.email || '',
            telefono: item.telefono || '',
            password: '',
            level: livelloDi(item) !== null ? livelloDi(item) : CONDOMINO,
            idUserGroup: refId(item.idUserGroup),
            idPalazzina: refId(item.idPalazzina),
            idAppartamento: refId(item.idAppartamento),
            abilitato: parseInt(item.abilitato, 10) === 1 ? 1 : 0,
            note: item.note || ''
        });
    }, [item]);

    /**
     * Ruoli proponibili: un admin di condominio non nomina admin globali.
     * Se sta modificando un utente di livello superiore il campo resta
     * visibile ma bloccato, così il salvataggio non lo declassa.
     */
    const livelliForm = useMemo(() => livelliAssegnabili(utenteCorrente), [utenteCorrente]);
    const ruoloBloccato = Boolean(form) && !livelliForm.some(l => l.value === parseInt(form.level, 10));

    /** Per un amministratore lo stato non e' una scelta: e' abilitato. */
    const sempreAbilitato = Boolean(form) && !richiedeApprovazione(form.level);

    /*
     * Quanto si chiede a una persona dipende da cosa sara'.
     *
     * Un amministratore globale non appartiene a niente: il suo gruppo e' solo
     * il timbro con cui nasce tutto quello che crea. Un amministratore di
     * condominio appartiene al suo condominio, e li' si ferma. Solo il
     * condomino ci abita davvero, e solo per lui palazzina e appartamento
     * hanno senso (facoltativi: per prenotare non servono) — per gli altri
     * due sarebbero una domanda senza una risposta giusta.
     */
    const livelloForm = form ? parseInt(form.level, 10) : null;
    const serveCondominio = globale && livelloForm !== null && livelloForm !== ADMIN_GLOBALE;
    const serveUnita = livelloForm === CONDOMINO;

    /**
     * Palazzine del condominio scelto. L'amministratore globale le ha davanti
     * tutte, di tutti i condomini: senza questo filtro potrebbe assegnare a
     * una persona un indirizzo che sta da un'altra parte.
     */
    const palazzineForm = useMemo(() => {
        if (!form || !serveCondominio) return palazzine.items;
        const idGruppo = parseInt(form.idUserGroup, 10) || 0;
        if (!idGruppo) return [];
        return palazzine.items.filter(p => refId(p.idUserGroup) === idGruppo);
    }, [form, serveCondominio, palazzine.items]);

    /** Appartamenti della palazzina selezionata, o di tutte quelle proponibili. */
    const appartamentiForm = useMemo(() => {
        if (!form) return [];
        const idPal = parseInt(form.idPalazzina, 10) || 0;
        if (idPal) return appartamenti.items.filter(a => refId(a.idPalazzina) === idPal);
        // chi ha un condominio solo li ha gia' tutti buoni: non c'e' da filtrare
        if (!serveCondominio) return appartamenti.items;
        const ammesse = palazzineForm.map(p => parseInt(p.id, 10));
        return appartamenti.items.filter(a => ammesse.includes(refId(a.idPalazzina)));
    }, [form, serveCondominio, appartamenti.items, palazzineForm]);

    function aggiorna(campo, valore) {
        setForm(prev => {
            const next = { ...prev, [campo]: valore };
            // cambiando palazzina l'appartamento precedente non e' piu' valido
            if (campo === 'idPalazzina') next.idAppartamento = 0;
            /* Cambiando condominio non lo sono piu' nemmeno palazzina e
               appartamento: stanno nel condominio di prima e non lo seguono. */
            if (campo === 'idUserGroup') {
                next.idPalazzina = 0;
                next.idAppartamento = 0;
            }
            return next;
        });
    }

    async function salva(e) {
        e.preventDefault();
        if (!form.name.trim() || !form.surname.trim() || !form.email.trim()) {
            toast.error('Nome, cognome ed email sono obbligatori.', { theme: 'colored' });
            return;
        }
        if (nuovo && !form.password) {
            toast.error('Imposta una password iniziale per il nuovo utente.', { theme: 'colored' });
            return;
        }
        if (serveCondominio && !parseInt(form.idUserGroup, 10)) {
            toast.error('Scegli il condominio a cui appartiene l\'utente.', { theme: 'colored' });
            return;
        }

        const email = form.email.trim();

        const payload = {
            name: form.name.trim(),
            surname: form.surname.trim(),
            email,
            // Username ed email sono la stessa cosa: il login accetta l'uno o
            // l'altra sullo stesso campo. Va riscritto a ogni salvataggio,
            // altrimenti cambiando l'email resterebbe in giro il vecchio
            // username, con cui si continuerebbe a entrare.
            username: email,
            telefono: form.telefono.trim(),
            level: optPayload(parseInt(form.level, 10)),
            /* Nascosti vuol dire vuoti: un utente promosso ad amministratore
               globale si porterebbe altrimenti dietro un'unita' che nessuno
               vede piu' e che non ha piu' senso di esistere. */
            idPalazzina: refPayload(serveUnita ? form.idPalazzina : 0),
            idAppartamento: refPayload(serveUnita ? form.idAppartamento : 0),
            abilitato: sempreAbilitato ? 1 : parseInt(form.abilitato, 10),
            note: form.note
        };
        // la password si invia solo quando viene effettivamente impostata
        if (form.password) payload.password = form.password;

        setSaving(true);
        try {
            const res = nuovo
                ? await DataManager.insertItem('user', payload)
                : await DataManager.updateItem('user', payload, item.id);

            if (!res || res.success !== 1) {
                toast.error(messaggioErrore(res, 'Salvataggio non riuscito.'), { theme: 'colored' });
                return;
            }

            // la create risponde con l'id in cima, non dentro `body`
            const idUtente = nuovo ? parseInt(res.id, 10) : item.id;

            /* Il condominio e' un secondo giro: la CRUD ha appena timbrato
               l'utente con il gruppo di chi scrive, che per l'amministratore
               globale e' sempre Gruppo YEC. */
            const scelto = parseInt(form.idUserGroup, 10) || 0;
            const attuale = nuovo ? 0 : refId(item.idUserGroup);
            let assegnato = true;

            if (serveCondominio && idUtente && scelto && scelto !== attuale) {
                const esito = await UtentiManager.assegnaUtente(idUtente, scelto);
                assegnato = Boolean(esito && esito.success === 1);
                if (!assegnato) {
                    toast.error(
                        messaggioErrore(esito, 'Utente salvato, ma non assegnato al condominio.'),
                        { theme: 'colored' }
                    );
                }
                else if (!nuovo) reload();
            }

            if (assegnato) {
                toast.success(nuovo ? 'Utente creato.' : 'Utente aggiornato.', { theme: 'colored' });
                navigate('/admin/utenti');
            }
            /* L'utente e' stato creato comunque: tornare indietro vorrebbe dire
               lasciarlo nel condominio sbagliato senza piu' un modo per
               accorgersene. Si resta sulla sua scheda, dove il campo e' li'
               pronto per riprovare. */
            else if (nuovo && idUtente) navigate(`/admin/utenti/${idUtente}`);
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setSaving(false);
        }
    }

    async function toggleAbilitazione() {
        const valore = parseInt(form.abilitato, 10) === 1 ? 0 : 1;
        try {
            const res = await DataManager.updateItem('user', { abilitato: valore }, item.id);
            if (res && res.success === 1) {
                toast.success(valore ? 'Utente abilitato.' : 'Utente disabilitato.', { theme: 'colored' });
                aggiorna('abilitato', valore);
                reload();
            }
            else {
                toast.error(messaggioErrore(res), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
    }

    async function elimina() {
        setWorking(true);
        try {
            const res = await DataManager.deleteItem('user', item.id);
            if (res && res.success === 1) {
                toast.success('Utente eliminato.', { theme: 'colored' });
                navigate('/admin/utenti');
            }
            else {
                toast.error(messaggioErrore(res), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setWorking(false);
        }
    }

    /* Le credenziali si mandano ai condomini e agli amministratori, ma non a
       se stessi ne' a chi non ha ancora un'email salvata. A un amministratore
       globale le manda solo un altro amministratore globale. */
    const inviabili = Boolean(item) && !nuovo
        && item.id !== parseInt(utenteCorrente.id, 10)
        && (livelloDi(item) !== ADMIN_GLOBALE || globale)
        && Boolean(item.email);

    async function inviaCredenziali() {
        setInviando(true);
        try {
            const res = await UtentiManager.inviaCredenziali({ id: item.id });
            if (res && res.success === 1 && res.data && res.data.inviate > 0) {
                toast.success(res.message, { theme: 'colored' });
                setDaInviare(false);
                reload();
            }
            else {
                toast.error(messaggioErrore(res, 'Invio non riuscito.'), { theme: 'colored' });
            }
        }
        catch (err) {
            toast.error('Impossibile contattare il server.', { theme: 'colored' });
        }
        finally {
            setInviando(false);
        }
    }

    const stato = statoLista({ loading: loading, error: error, empty: !form, icon: 'user' });

    return (
        <DetailPage
            backTo="/admin/utenti"
            backLabel="Torna all'anagrafica"
            eyebrow="Amministrazione utenti"
            title={nuovo ? 'Nuovo utente' : (item ? nomeUtente(item) : 'Utente')}
            subtitle={!nuovo && item
                ? `${etichettaLivello(item.level)} · ${parseInt(item.abilitato, 10) === 1 ? 'abilitato' : 'da abilitare'}`
                    + ` · ${item.credenziali_inviate_il
                        ? `credenziali inviate il ${formatDateTime(item.credenziali_inviate_il)}`
                        : 'credenziali mai inviate'}`
                : 'Anagrafica, contatti e, per i condomini, palazzina e appartamento.'}
        >
            {stato || (
                <form onSubmit={salva}>
                    <div className="lv-form-grid">
                        <Field label="Nome" required>
                            <input
                                className="lv-input"
                                value={form.name}
                                onChange={e => aggiorna('name', e.target.value)}
                            />
                        </Field>
                        <Field label="Cognome" required>
                            <input
                                className="lv-input"
                                value={form.surname}
                                onChange={e => aggiorna('surname', e.target.value)}
                            />
                        </Field>
                        <Field label="Email" required hint="Usata anche come nome utente per l'accesso.">
                            <input
                                type="email"
                                className="lv-input"
                                value={form.email}
                                onChange={e => aggiorna('email', e.target.value)}
                            />
                        </Field>
                        <Field label="Telefono">
                            <input
                                className="lv-input"
                                value={form.telefono}
                                onChange={e => aggiorna('telefono', e.target.value)}
                            />
                        </Field>
                        <Field
                            label="Stato"
                            hint={sempreAbilitato ? "Un amministratore non passa dall'approvazione." : undefined}
                        >
                            <Select
                                value={sempreAbilitato ? 1 : form.abilitato}
                                disabled={sempreAbilitato}
                                onChange={v => aggiorna('abilitato', v)}
                                options={[
                                    { value: '1', label: 'Abilitato' },
                                    { value: '0', label: 'Da abilitare' }
                                ]}
                            />
                        </Field>
                        <Field
                            label="Ruolo"
                            hint={ruoloBloccato ? 'Solo un amministratore globale può cambiare questo ruolo.' : undefined}
                        >
                            <Select
                                value={form.level}
                                disabled={ruoloBloccato}
                                onChange={v => aggiorna('level', v)}
                                options={ruoloBloccato
                                    ? [{ value: String(form.level), label: etichettaLivello(form.level) }]
                                    : livelliForm.map(l => ({ value: String(l.value), label: l.label }))}
                            />
                        </Field>
                        {serveCondominio && (
                            <Field
                                label="Condominio"
                                required
                                hint="Decide le sale che potrà prenotare e chi lo trova in anagrafica."
                            >
                                <Select
                                    value={form.idUserGroup}
                                    onChange={v => aggiorna('idUserGroup', v)}
                                    options={[
                                        { value: '0', label: 'Scegli il condominio…' },
                                        ...condomini.items.map(g => ({ value: String(g.id), label: g.name }))
                                    ]}
                                />
                            </Field>
                        )}
                        {serveUnita && (
                            <Field
                                label="Palazzina"
                                hint={serveCondominio && !parseInt(form.idUserGroup, 10)
                                    ? 'Scegli prima il condominio: le palazzine sono le sue.'
                                    : undefined}
                            >
                                <Select
                                    value={form.idPalazzina}
                                    onChange={v => aggiorna('idPalazzina', v)}
                                    options={[
                                        { value: '0', label: 'Non assegnata' },
                                        ...palazzineForm.map(p => ({ value: String(p.id), label: p.nome }))
                                    ]}
                                />
                            </Field>
                        )}
                        {serveUnita && (
                            <Field label="Appartamento">
                                <Select
                                    value={form.idAppartamento}
                                    onChange={v => aggiorna('idAppartamento', v)}
                                    options={[
                                        { value: '0', label: 'Non assegnato' },
                                        ...appartamentiForm.map(a => ({ value: String(a.id), label: a.identificativo }))
                                    ]}
                                />
                            </Field>
                        )}
                        <Field
                            label={nuovo ? 'Password iniziale' : 'Nuova password'}
                            required={nuovo}
                            hint={nuovo ? undefined : 'Lascia vuoto per non modificarla.'}
                        >
                            <input
                                type="password"
                                className="lv-input"
                                value={form.password}
                                onChange={e => aggiorna('password', e.target.value)}
                            />
                        </Field>
                        <Field label="Note" wide>
                            <textarea
                                className="lv-input"
                                rows={2}
                                value={form.note}
                                onChange={e => aggiorna('note', e.target.value)}
                            />
                        </Field>
                    </div>

                    <DetailActions saving={saving} submitLabel={nuovo ? 'Crea utente' : 'Salva modifiche'}>
                        {inviabili && (
                            <button type="button" className="lv-btn lv-btn-ghost" onClick={() => setDaInviare(true)}>
                                <FontAwesomeIcon icon={['fas', 'envelope']} className="me-2" />
                                {item.credenziali_inviate_il ? 'Rimanda credenziali' : 'Invia credenziali'}
                            </button>
                        )}
                        {!nuovo && richiedeApprovazione(form.level) && (
                            <button type="button" className="lv-btn lv-btn-ghost" onClick={toggleAbilitazione}>
                                {parseInt(form.abilitato, 10) === 1 ? 'Disabilita' : 'Abilita'}
                            </button>
                        )}
                        {!nuovo && (
                            <button
                                type="button"
                                className="lv-btn lv-btn-ghost lv-btn-danger-ghost"
                                onClick={() => setDaEliminare(true)}
                            >
                                <FontAwesomeIcon icon={['fas', 'trash']} className="me-2" />
                                Elimina
                            </button>
                        )}
                    </DetailActions>
                </form>
            )}

            <ConfirmModal
                show={daInviare}
                title="Inviare le credenziali?"
                message={item
                    ? `A ${nomeUtente(item)} arriverà su ${item.email} una password provvisoria nuova. Quella che usa adesso smetterà di funzionare.`
                    : ''}
                confirmLabel="Invia"
                working={inviando}
                onConfirm={inviaCredenziali}
                onClose={() => setDaInviare(false)}
            />

            <ConfirmModal
                show={daEliminare}
                title="Eliminare l'utente?"
                message={item
                    ? `${nomeUtente(item)} non potrà più accedere. Le prenotazioni già registrate restano.`
                    : ''}
                confirmLabel="Elimina"
                danger
                working={working}
                onConfirm={elimina}
                onClose={() => setDaEliminare(false)}
            />
        </DetailPage>
    );
}
